"use client";

import React, { useCallback, useRef, useState } from "react";
import PopupForm from "@/components/newPageComponent/CommonSection/PopupForm";

import { getFormOneDataFromLocal } from "@/lib/edcrypt";
import {
  isLogo,
  logoSrc,
  logoSrcSet,
  resolveLogoHeight,
  LOGO_IMG_CLASS,
  LOGO_SLOT_CLASS,
  type LogoSizing,
} from "@/components/newPageComponent/CommonSection/companyLogo";

const LOCAL_STORE_NAME = "MILES_NEXT_F1_REF_ENQUIRY";

type Tone = "part-1" | "part-2";

/** [label, value, tone] — rendered as "value label" inside the score pill. */
type Score = [string, string, Tone];

type TestimonialCard = {
  name: string;
  designation: string;
  /** Logo URL where the employer has a mark, otherwise the employer name. */
  company: string;
  text: string;
  scores: Score[];
  profilePic: string;
} & LogoSizing;

/** Alumni shots are square, so the same crop serves every card. */
const AVATAR = "?tr=f-webp,q-70,w-200,h-200,fo-top";

export const cards: TestimonialCard[] = [
  {
    name: "Shanif Nizar",
    designation: "Tax Associate",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/kpmg_logo.png?tr=f-webp,q-70,h-48",
    logoShape: "compact",
    logoHeight: 27,
    text: "I felt a lot more confident during placements because of how Miles prepared me overall. Along with their support i was able to get into KPMG.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Shanif%20Nizar.png${AVATAR}`,
    scores: [
      ["Part 1", "430", "part-1"],
      ["Part 2", "460", "part-2"],
    ],
  },
  {
    name: "Ishtaa Jain",
    designation: "Audit Associate",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/ey_logo.png?updatedAt=1787047568559",
    logoShape: "stacked",
    text: "The clarity Varun Jain sir gave me is still my edge when solving audit problems at EY. That confidence, combined with the placement guidance at Miles, is how I ended up at EY.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Ishtaa%20Jain.png${AVATAR}`,
    scores: [
      ["Part 1", "430", "part-1"],
      ["Part 2", "440", "part-2"],
    ],
  },
  {
    name: "Swapan Singla",
    designation: "Executive",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/wipro_logo.png?updatedAt=1787043142539",
    logoShape: "stacked",
    text: "When placements started, the way their team guided me made all the difference in getting into Wipro. I walked into Wipro already thinking like an analyst, thanks to how practically Miles trained us during CMA.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Swapan%20Singla.png${AVATAR}`,
    scores: [
      ["Part 1", "430", "part-1"],
      ["Part 2", "420", "part-2"],
    ],
  },
  {
    name: "Tanu Priya Sharma",
    designation: "Associate Consultant",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/kpmg_logo.png?tr=f-webp,q-70,h-48",
    logoShape: "compact",
    logoHeight: 27,
    text: "Miles prepared me for more than clearing CMA, the fundamentals they built are what make me confident consulting clients at KPMG today.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Tanu%20Priya%20Sharma.png${AVATAR}`,
    scores: [
      ["Part 1", "430", "part-1"],
      ["Part 2", "410", "part-2"],
    ],
  },
  {
    name: "Gaurang Asopa",
    designation: "Senior Analyst",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/goldman.webp",
    logoShape: "compact",
    text: "I still remember how Varun Jain sir explained a few tricky topics during my CMA prep at Miles, and that really stuck with me. That clarity, along with how the Miles team handled placements, is what eventually got me into Goldman Sachs.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Gaurang%20Asopa.png${AVATAR}`,
    scores: [
      ["Part 1", "410", "part-1"],
      ["Part 2", "450", "part-2"],
    ],
  },
  {
    name: "Tushar Saini",
    designation: "Management Trainee",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/wipro_logo.png?updatedAt=1787043142539",
    logoShape: "stacked",
    text: "Miles made CMA concepts stick so well that I'm applying them daily at Wipro, not just recalling them for an exam and during placements, their support played a big part in helping me get into Wipro.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Tushar%20Saini.png${AVATAR}`,
    scores: [
      ["Part 1", "400", "part-1"],
      ["Part 2", "440", "part-2"],
    ],
  },
  {
    name: "Abhilash Ashokan Vadassery",
    designation: "Senior Finance Analyst",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/Warner-Brothers.webp",
    logoShape: "stacked",
    text: "Miles kept my CMA prep structured, and that same structured thinking is what makes me efficient at Warner Bros today.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Abhilash%20Ashokan%20Vadassery.png${AVATAR}`,
    scores: [
      ["Part 1", "430", "part-1"],
      ["Part 2", "410", "part-2"],
    ],
  },
  {
    name: "Abinash S",
    designation: "Tax Consultant",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/kpmg_logo.png?tr=f-webp,q-70,h-48",
    logoShape: "compact",
    logoHeight: 27,
    text: "The routine Miles built during my CMA prep is the same discipline that keeps me organized. And at the time of placements, their involvement helped me secure an opportunity at KPMG.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Abinash%20S.png${AVATAR}`,
    scores: [
      ["Part 1", "430", "part-1"],
      ["Part 2", "430", "part-2"],
    ],
  },
  {
    name: "Aniketh Manjunatha",
    designation: "Executive",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/wipro_logo.png?updatedAt=1787043142539",
    logoShape: "stacked",
    text: "460 score in one of the parts really gave me confidence and it's all thanks to Miles's placement support system that helped me get into Wipro",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Aniketh%20Manjunatha.png${AVATAR}`,
    scores: [
      ["Part 1", "430", "part-1"],
      ["Part 2", "460", "part-2"],
    ],
  },
  {
    name: "Eshan Parashar",
    designation: "Fund Accountant",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/ssc_logo.png?updatedAt=1787049165889",
    logoShape: "stacked",
    text: "Miles kept me steady through CMA, and that steadiness is what helps me handle high-pressure reconciliations at SS&C GlobeOp.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Eshan%20Parashar.png${AVATAR}`,
    scores: [
      ["Part 1", "400", "part-1"],
      ["Part 2", "400", "part-2"],
    ],
  },
  {
    name: "Kavitha Nair",
    designation: "Audit Associate",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/grant_thornton_logo.png?updatedAt=1787047568670",
    text: "Scoring 440 in both parts was honestly a big moment for me, and Miles played a huge role in that. The transition into placements was just as smooth, and that's how I got into Grant Thornton.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Kavitha%20Nair.png${AVATAR}`,
    scores: [
      ["Part 1", "440", "part-1"],
      ["Part 2", "440", "part-2"],
    ],
  },
  {
    name: "Simran Saini",
    designation: "Tax Senior Financial Consultant",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/eisner_amper_logo.png?updatedAt=1787049165439",
    logoShape: "compact",
    text: "Miles's systems for consistency is what helped me get into EisnerAmper. Scoring around 420 and 400 just reflected the consistency I built over time along with their placement efforts of course.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Simran%20Saini.png${AVATAR}`,
    scores: [
      ["Part 1", "420", "part-1"],
      ["Part 2", "400", "part-2"],
    ],
  },
  {
    name: "Krishnan P N E",
    designation: "Associate",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/kpmg_logo.png?tr=f-webp,q-70,h-48",
    logoShape: "compact",
    logoHeight: 27,
    text: "Scoring around 430 and 410 felt like I was finally on track with CMA. The consistency I built with Miles across both CMA parts keeps me sharp handling client deliverables as an Associate at KPMG.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Krishnan%20P%20N%20E.png${AVATAR}`,
    scores: [
      ["Part 1", "430", "part-1"],
      ["Part 2", "410", "part-2"],
    ],
  },
  {
    name: "Arjun Kris Saksena",
    designation: "Retainer",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/wipro_logo.png?updatedAt=1787043142539",
    logoShape: "stacked",
    text: "Scoring 460 in the second part was definitely a highlight for me, Miles supported my overall growth that keeps me competitive at Wipro today.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/ARJUN%20KRIS%20SAKSENA.png${AVATAR}`,
    scores: [
      ["Part 1", "430", "part-1"],
      ["Part 2", "460", "part-2"],
    ],
  },
  {
    name: "Venkat Supreeth S",
    designation: "Finance Intern",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/hewlett_packard_logo.png?updatedAt=1787047568537",
    logoShape: "compact",
    text: "Scoring 450 in Part 1 was a big boost for me, I didn't expect it honestly. It gave me a problem solving edge that's already visible in my internship at HPE.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Venkat%20Supreeth%20S.png${AVATAR}`,
    scores: [
      ["Part 1", "450", "part-1"],
      ["Part 2", "400", "part-2"],
    ],
  },
  {
    name: "Abu Tahir M",
    designation: "SAP Financial Accounting Consultant",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/IBM.webp",
    logoShape: "compact",
    text: "Even during challenging phases of my preparation, the consistent support and guidance at Miles helped me stay on track. That resilience, along with their placement support, helped me begin my journey at IBM",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Abu%20Tahir%20M.png${AVATAR}`,
    scores: [
      ["Part 1", "440", "part-1"],
      ["Part 2", "420", "part-2"],
    ],
  },
  {
    name: "Vishal Goel",
    designation: "Sr. Fund Accountant",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/kpmg_logo.png?tr=f-webp,q-70,h-48",
    logoShape: "compact",
    logoHeight: 27,
    text: "The placement support of Miles helped me get into Wipro without having any extraordinary scores, the only thing that mattered was my consistency.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Vishal%20Goel.png${AVATAR}`,
    scores: [
      ["Part 1", "400", "part-1"],
      ["Part 2", "410", "part-2"],
    ],
  },
  {
    name: "Gopika Lakshmi V",
    designation: "Retainer",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/wipro_logo.png?updatedAt=1787043142539",
    logoShape: "stacked",
    text: "Miles' placement support gave me the opportunity to start my career at Wipro. The consistency and discipline they helped me build throughout both CMA parts continue to keep me reliable and sharp in my role today.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Gopika%20Lakshmi%20V.png${AVATAR}`,
    scores: [
      ["Part 1", "450", "part-1"],
      ["Part 2", "430", "part-2"],
    ],
  },
  {
    name: "Abdallah Yasir",
    designation: "Senior Executive",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/pepsico_logo.png?updatedAt=1787043142081",
    text: "Placements were something I was a bit unsure about, but Miles handled that really well. I walked into PepsiCo being fully industry ready without any worries!",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Abdallah%20Yasir.png${AVATAR}`,
    scores: [
      ["Part 1", "400", "part-1"],
      ["Part 2", "410", "part-2"],
    ],
  },
  {
    name: "S Sankalp",
    designation: "Management Trainee",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/hewlett_packard_logo.png?updatedAt=1787047568537",
    logoShape: "compact",
    text: "Landing my role as a Management Trainee at HPE was possible because of Miles' placement support, but what has helped me succeed in the role is the consistency I built while scoring around 430 in both CMA parts.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/S%20Sankalp.png${AVATAR}`,
    scores: [
      ["Part 1", "430", "part-1"],
      ["Part 2", "430", "part-2"],
    ],
  },
  {
    name: "Vishnu Vardhan",
    designation: "KYC Analyst",
    company:
      "https://ik.imagekit.io/mileseducation/miles_website/company/grant_thornton_logo.png?updatedAt=1787047568670",
    text: "Miles support never made the journey feel solitary, especially during the placements, and that confidence is what helps me handle complex KYC cases at Grant Thornton independently.",
    profilePic: `https://ik.imagekit.io/mileseducation/miles_website/home_2026/alumni/Vishnu%20Vardhan.png${AVATAR}`,
    scores: [
      ["Part 1", "400", "part-1"],
      ["Part 2", "410", "part-2"],
    ],
  },
];

const NavButton = ({
  direction,
  onClick,
}: {
  direction: "prev" | "next";
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={direction === "prev" ? "Previous story" : "Next story"}
    className={`hidden md:flex absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full border border-[#2f89ea] text-[#2f89ea] bg-white transition-colors hover:bg-[#2f89ea] hover:text-white ${
      direction === "prev" ? "left-0" : "right-0"
    }`}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      {direction === "prev" ? (
        <path d="M19 12H5m0 0 6-6m-6 6 6 6" />
      ) : (
        <path d="M5 12h14m0 0-6-6m6 6-6 6" />
      )}
    </svg>
  </button>
);

const CardItem = ({ card, uid }: { card: TestimonialCard; uid: string }) => {
  const logoHeight = resolveLogoHeight(card);

  return (
    <article
      className="h-full flex flex-col items-center text-center bg-white rounded-2xl shadow-[0_5px_20px_-4px_rgba(0,0,0,0.12),0_0_8px_rgba(0,0,0,0.05)] px-4 py-4"
      aria-label={`Testimonial by ${card.name}`}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- ImageKit URLs
        already carry their transforms, so no loader is needed. */}
      <img
        src={card.profilePic}
        alt={card.name}
        width={100}
        height={100}
        loading="lazy"
        decoding="async"
        className="w-[85px] h-[85px] md:w-[100px] md:h-[100px] rounded-full object-cover"
      />

      <h3 className="mt-4 text-[#3a73ac] text-base md:text-[20px] font-bold leading-tight">
        {card.name}
      </h3>
      {card.designation && (
        <p className="text-[#3a73ac] text-sm md:text-base font-semibold">
          {card.designation}
        </p>
      )}

      <span className={LOGO_SLOT_CLASS}>
        {isLogo(card.company) ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={logoSrc(card.company, logoHeight)}
            srcSet={logoSrcSet(card.company, logoHeight)}
            alt={card.name}
            loading="lazy"
            decoding="async"
            style={{ maxHeight: logoHeight }}
            className={LOGO_IMG_CLASS}
          />
        ) : (
          <span className="text-sm font-bold text-[#333]">{card.company}</span>
        )}
      </span>

      {/* Clamped because the quotes run from one line to four sentences, and
        items-stretch means the longest one would set the height of them all. */}
      <p className="mt-3 text-black text-xs md:text-sm leading-[22px] line-clamp-5">
        {card.text}
      </p>

      <div className="flex flex-wrap justify-center gap-2 mt-auto pt-5">
        {card.scores.map(([label, value]) => (
          <span
            key={`${uid}-${label}`}
            className="bg-[#2f6fb3] text-white text-[10px] font-bold rounded-full px-2.5 py-1 whitespace-nowrap"
          >
            {value} {label}
          </span>
        ))}
      </div>
    </article>
  );
};

const AboutCMA = () => {
  const [isFormPopupOpen, setFormPopupOpen] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const scrollByCard = useCallback((direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 0) return;

    const card = track.firstElementChild as HTMLElement | null;
    const step = card ? card.offsetWidth : track.clientWidth / 3;

    let next = track.scrollLeft + direction * step;
    // 1px of tolerance for sub-pixel scroll positions.
    if (next > maxScroll + 1) {
      next = 0;
    } else if (next < -1) {
      next = maxScroll;
    }

    track.scrollTo({ left: next, behavior: "smooth" });
  }, []);

  const scrollPrev = useCallback(() => scrollByCard(-1), [scrollByCard]);
  const scrollNext = useCallback(() => scrollByCard(1), [scrollByCard]);

  const openFormPopup = (redirectUrl: string) => {
    const { status: isFormOneFilled } =
      getFormOneDataFromLocal(LOCAL_STORE_NAME);
    if (isFormOneFilled) {
      window.open(redirectUrl, "_blank");
      return;
    }
    sessionStorage.setItem("modal-open", "true");
    setFormPopupOpen(true);
  };

  const closeFormPopup = () => {
    sessionStorage.removeItem("modal-open");
    setFormPopupOpen(false);
  };

  const handleJoinCommunity = () =>
    openFormPopup("https://www.linkedin.com/school/mileseducation/");

  const handleEligibilityCheck = () =>
    openFormPopup("https://cpa-course.mileseducation.com/eligibility");

  return (
    <>
      <section
        className="max-md:px-4 max-md:pt-6 bg_white_new md:mb-0 mb-4"
        aria-labelledby="about-cpa-title"
      >
        <div className="main_container py-[50px] max-md:px-0 max-md:pt-5 max-md:pb-10 max-md:bg-white max-md:rounded-2xl">
          <div>
            <h2
              id="about-cpa-title"
              className="m-0 text-center text-[32px] leading-[1.15] font-bold tracking-[-0.02em] text-[#333] max-md:text-2xl max-md:px-4 [&>span]:text-[#2f89ea]"
            >
              <span>World&rsquo;s Largest CMA</span> Course Provider
            </h2>

            <p className="max-w-[636px] mx-auto my-8 text-center text-sm leading-[1.6] text-[#999] font-medium max-md:my-4 max-md:px-4 [&>span]:font-bold">
              Meet CMAs across India who transformed their careers through{" "}
              <span>
                Miles Education&rsquo;s world-class LMS, engaging video lessons,
                and expert 1-on-1 mentorship
              </span>
            </p>
          </div>

          <div className="relative max-md:pt-1 md:px-14">
            <NavButton direction="prev" onClick={scrollPrev} />

            <div
              ref={trackRef}
              className="scrollbar-hide flex items-stretch snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain px-[calc((100%-280px)/2)] md:px-0 md:mx-auto md:max-w-[1128px]"
            >
              {cards.map((card, i) => (
                <div
                  key={`story-${i}`}
                  className="w-[280px] md:w-[376px] shrink-0 snap-center md:snap-start px-2 md:px-3 py-4"
                >
                  <CardItem card={card} uid={`story-${i}`} />
                </div>
              ))}
            </div>

            <NavButton direction="next" onClick={scrollNext} />
          </div>

          <p className="md:hidden mt-2 text-center text-xs font-bold text-[#3A73AC]">
            &larr; Swipe to explore &rarr;
          </p>

          <div>
            <div className="mt-8 max-md:mt-5 flex justify-center items-center gap-7 flex-wrap max-md:flex-col max-md:gap-3.5 max-md:px-5">
              <button
                onClick={handleJoinCommunity}
                className="text-[15px] font-bold no-underline cursor-pointer transition-all duration-200 hover:opacity-90 hover:-translate-y-px rounded-tl-xl rounded-tr-xl rounded-br-xl rounded-bl-none bg-[#2a85ff] text-white px-9 py-2.5 max-md:text-sm max-md:w-full max-md:text-center max-md:box-border"
              >
                Join the Community
              </button>

              <button
                onClick={handleEligibilityCheck}
                className="text-[15px] font-bold cursor-pointer transition-all duration-200 hover:opacity-90 hover:-translate-y-px text-[#2f7fe9] underline underline-offset-4 max-md:text-sm"
              >
                Get a Free Eligibility Check
              </button>
            </div>
          </div>
        </div>
      </section>

      {isFormPopupOpen && (
        <PopupForm handleClose={closeFormPopup} pageName="CMA" />
      )}
    </>
  );
};

export default AboutCMA;
